import type { SelectOption } from '@/components/form/fields'
import type {
  AttendanceStatus,
  DayOfWeek,
  GroupPosition,
  GroupStatus,
  MeetingFrequency,
  MembershipStatus,
} from '@/api/types'

/**
 * The group enumerations, spelled exactly as the API serialises them. The label is what an
 * operator reads and the value is what crosses the wire; keeping them apart lets the wording be
 * improved without changing what is sent.
 */

export const GROUP_STATUSES = [
  'Forming',
  'Active',
  'Suspended',
  'Dissolved',
] as const satisfies readonly GroupStatus[]

/** How a membership can end. `Active` is not an ending, so it is not offered. */
export const MEMBER_EXIT_STATUSES = [
  'Exited',
  'Expelled',
  'Deceased',
] as const satisfies readonly Exclude<MembershipStatus, 'Active'>[]

export const GROUP_POSITIONS = [
  'Member',
  'Secretary',
  'Leader',
] as const satisfies readonly GroupPosition[]

export const MEETING_FREQUENCIES = [
  'Weekly',
  'Fortnightly',
  'Monthly',
] as const satisfies readonly MeetingFrequency[]

export const ATTENDANCE_STATUSES = [
  'Present',
  'Late',
  'Absent',
  'Excused',
] as const satisfies readonly AttendanceStatus[]

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const satisfies readonly DayOfWeek[]

const GROUP_STATUS_LABELS: Record<GroupStatus, string> = {
  Forming: 'Forming',
  Active: 'Active',
  Suspended: 'Suspended',
  Dissolved: 'Dissolved',
}

const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatus, string> = {
  Active: 'Active',
  Exited: 'Exited',
  Expelled: 'Expelled',
  Deceased: 'Deceased',
}

export const groupStatusLabel = (status: GroupStatus) => GROUP_STATUS_LABELS[status]

export const membershipStatusLabel = (status: MembershipStatus) => MEMBERSHIP_STATUS_LABELS[status]

export const GROUP_POSITION_OPTIONS: SelectOption[] = GROUP_POSITIONS.map((value) => ({
  value,
  label: value,
}))

export const MEETING_FREQUENCY_OPTIONS: SelectOption[] = MEETING_FREQUENCIES.map((value) => ({
  value,
  label: value,
}))

export const DAY_OF_WEEK_OPTIONS: SelectOption[] = DAYS_OF_WEEK.map((value) => ({
  value,
  label: value,
}))

/**
 * What each way of ending a membership means, shown beside the choice. Expulsion and death are not
 * interchangeable with a member walking away, and the difference is what the next officer reads.
 */
export const MEMBER_EXIT_CONSEQUENCES: Record<Exclude<MembershipStatus, 'Active'>, string> = {
  Exited: 'The member left of their own accord. The membership closes on the date given.',
  Expelled: 'The group removed the member. The reason is what the next officer reads about it.',
  Deceased: 'The membership closes on the date of death. Any balances pass to the estate process.',
}

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  Present: 'Present',
  Late: 'Late',
  Absent: 'Absent',
  Excused: 'Excused',
}

/**
 * What each attendance mark does to the health score, shown where the mark is made.
 *
 * An excused absence is excluded from both sides of the ratio rather than counted against the
 * group, and saying so on the screen is the point: illness and funerals are ordinary in a village
 * banking book, and a group that believes honesty is penalised stops recording them.
 */
export const ATTENDANCE_STATUS_EFFECT: Record<AttendanceStatus, string> = {
  Present: 'Counts as attended.',
  Late: 'Counts as attended — arriving late is not the same as not coming.',
  Absent: 'Counts against the attendance ratio.',
  Excused: 'Excluded from both sides of the ratio, so it neither helps nor harms the group.',
}

/**
 * The stable codes a non-scored health outcome carries, in words. The first two are ordinary
 * states of a young book; the last three are hard overrides and travel with a score of zero, which
 * is a real answer rather than an absence.
 */
export const HEALTH_REASONS: Record<string, string> = {
  'groups.health.insufficient_history':
    'The window holds less than the minimum history, or no meetings have taken place yet.',
  'groups.health.credit_data_unavailable':
    'The loan book cannot answer for this window, so on-time repayment and portfolio at risk are unknown.',
  'groups.health.group_suspended': 'The group is suspended, which disqualifies it outright.',
  'groups.health.loan_written_off': 'A member loan was written off inside the window.',
  'groups.health.defaulters_over_limit':
    'More members are 90 or more days in arrears than the configuration allows.',
}

/** A fraction in 0–1 as a percentage, or a dash for a figure the source could not produce. */
export const formatRatio = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(1)}%`

/** The 0–100 health score, to one decimal place — the scale the qualifying threshold is on. */
export const formatScore = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : value.toFixed(1)

/**
 * A time of day, to the minute.
 *
 * The contract says `HH:mm`, and the API serialises .NET's `TimeOnly` as `HH:mm:ss`. A meeting
 * does not start at ten o'clock and no seconds, so the seconds are trimmed here rather than shown
 * — and trimmed rather than assumed away, so a value that really does carry seconds still reads
 * correctly.
 */
export const formatTimeOfDay = (value: string) => value.slice(0, 5)

/**
 * A schedule in one line: `Weekly on Wednesday at 10:00`, `Monthly on day 15 at 09:30`.
 * A monthly day is capped at 28 by the API, so this never has to explain a missing 31st.
 */
export function describeSchedule(schedule: {
  frequency: MeetingFrequency
  dayOfWeek: DayOfWeek | null
  dayOfMonth: number | null
  startTime: string
}): string {
  const when =
    schedule.frequency === 'Monthly'
      ? schedule.dayOfMonth === null
        ? ''
        : ` on day ${String(schedule.dayOfMonth)} of the month`
      : schedule.dayOfWeek === null
        ? ''
        : ` on ${schedule.dayOfWeek}`

  return `${schedule.frequency}${when} at ${formatTimeOfDay(schedule.startTime)}`
}
